<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "$lib/components/ui/accordion"
  import { Button } from "$lib/components/ui/button"
  import { Input } from "$lib/components/ui/input"
  import type { _response } from "$lib/data/type/_response"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { sleep } from "$lib/sleep"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import { createInfiniteQuery } from "@tanstack/svelte-query"
  import type { Faq } from "./_types"

  const prefix = $derived(page.params.prefix ?? "0")

  let sentinel = $state<HTMLDivElement>()

  const search = createSearch()
  const query = createInfiniteQuery(() => ({
    queryKey: ["faq", {
      prefix,
      search: search.debounced
    }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<Faq[]>>(`/api/${prefix}/info/faqs`, {
        params: {
          page: pageParam,
          search: search.debounced
        }
      })

      return response.data.result ?? []
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))
  const rows = $derived(query.data?.pages.flat() ?? [])

  async function handleRefresh() {
    await sleep(1)
    await query.refetch()
  }

  $effect(() => {
    if (!sentinel || !query.hasNextPage || query.isFetching) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void query.fetchNextPage()
      },
      { threshold: 0.5 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  })

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/home`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full">
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">FAQ</h1>
      </div>
      <div></div>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <Button
        variant="outline"
        size="icon"
        class="group shrink-0"
        data-loading={query.isFetching}
        disabled={query.isFetching}
        onclick={handleRefresh}>
        {#if query.isFetching}
          <LoaderCircleIcon
            class="animate-spin"
            aria-hidden="true"/>
        {:else}
          <RefreshCwIcon aria-hidden="true"/>
        {/if}
      </Button>
      <Input
        type="text"
        class="min-w-0 flex-1"
        autocapitalize="off"
        placeholder="Cari..."
        value={search.state}
        oninput={(event) => search.set(event.currentTarget.value)}/>
    </div>
  </nav>
  <div class="min-h-full px-3">
    {#if query.isError}
      <div class="flex flex-col items-center gap-2 px-3 py-4 text-center text-xs">
        Gagal memuat data
        <Button
          variant="outline"
          onclick={() => query.refetch()}>
          Muat Ulang
        </Button>
      </div>
    {:else if rows.isEmpty() && query.isFetching}
      <div class="w-full space-y-1 py-2 text-xs">
        <div class="h-4 w-56 animate-pulse rounded bg-accent"></div>
        <div class="h-4 w-36 animate-pulse rounded bg-accent"></div>
      </div>
    {:else if rows.isEmpty()}
      <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
    {:else}
      <Accordion type="single">
        {#each rows as faq (faq.id)}
          <AccordionItem value={String(faq.id)}>
            <AccordionTrigger class="py-3 text-start font-normal">
              {faq.name}
            </AccordionTrigger>
            <AccordionContent class="pb-3">{faq.description}</AccordionContent>
          </AccordionItem>
        {/each}
      </Accordion>
    {/if}
  </div>
  <div
    bind:this={sentinel}
    class="h-1">
  </div>
</main>
